import React, { useState } from 'react';
import { authService } from '../lib/auth';
import { recordTwoFactorVerification, isTwoFactorFresh } from '../lib/twoFactorSessionCache';
import { useSoftKeyboard } from '../contexts/SoftKeyboardContext';
import { isManager, canLoginWithEmailPassword } from '../lib/authUtils';

interface SignInPageProps {
  onSignIn: (authUserId: string, staffData: any) => void;
  onSetupMode?: (staffData: any) => void; // For staff without auth_user_id
}

const SignInPage: React.FC<SignInPageProps> = ({ onSignIn, onSetupMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(new Date());
  const [setupMode, setSetupMode] = useState(false);
  const [staffData, setStaffData] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Skip pre-sign-in staff lookup: Firestore staff collection requires auth (custom claims or own-email read).
  // Setup mode is handled when user tries to sign in (user-not-found or after sign-in).

  const { enabled: softKeyboardEnabled, openKeyboard } = useSoftKeyboard();

  const applyEmail = (value: string) => setEmail(value);
  const applyPassword = (value: string) => setPassword(value);
  const applyNewPassword = (value: string) => setNewPassword(value);
  const applyConfirmPassword = (value: string) => setConfirmPassword(value);
  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!password) {
        setError('Password is required');
        setLoading(false);
        return;
      }

      // Sign in first so we have an authenticated session (token will include custom claims after refresh)
      const { user, error: signInError } = await authService.signIn({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message || 'Invalid email or password');
        setLoading(false);
        return;
      }

      if (!user) {
        setError('Login failed');
        setLoading(false);
        return;
      }

      const authUserId = (user as any).uid || user.id;
      if (!authUserId) {
        setError('Failed to get user ID');
        setLoading(false);
        return;
      }

      // Now authenticated; token was refreshed in signIn() so staff read should succeed
      let staffRecord = await authService.getStaffByAuthUserId(authUserId);
      if (!staffRecord && (user as any).email) {
        staffRecord = await authService.getStaffByEmail((user as any).email);
      }

      if (!staffRecord) {
        setError('Staff record not found. Please contact administrator.');
        await authService.signOut();
        setLoading(false);
        return;
      }

      // Check if staff member can login with email/password
      if (!canLoginWithEmailPassword(staffRecord)) {
        await authService.signOut();
        setError('This role cannot login with email/password. Please use PIN entry to access the system.');
        setLoading(false);
        return;
      }

      await completeSignIn(authUserId);
    } catch (err) {
      console.error('Sign in error:', err);
      setError('Login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleSetupPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // First, try to sign in (in case auth user already exists)
      const { user: existingUser, error: signInError } = await authService.signIn({
        email: staffData.email,
        password: newPassword,
      });

      if (existingUser) {
        // Auth user already exists and password works - just sign in
        await completeSignIn((existingUser as any).uid || existingUser.id);
        return;
      }

      // If sign-in failed, try to create new auth user
        const existingStaffDocId = staffData?.firestoreId || staffData?.id;
        const { user, error: signUpError } = await authService.signUp({
        email: staffData.email,
        password: newPassword,
        fullName: staffData.name,
        role: staffData.role,
        pin: staffData.pin || '0000',
        }, existingStaffDocId); // Pass existing Firestore document ID

      if (signUpError) {
        // If email already in use, inform user to use regular sign-in
        if (signUpError.code === 'auth/email-already-in-use' || signUpError.message?.includes('email-already-in-use')) {
          setError('This email already has an account. Please use the sign-in form below with your existing password.');
          setSetupMode(false); // Exit setup mode
        } else {
          setError(signUpError.message || 'Failed to create account');
        }
        setLoading(false);
        return;
      }

      if (!user) {
        setError('Account creation failed');
        setLoading(false);
        return;
      }

      // Wait a moment for the database update to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Sign in with the new account
      const authUserId = (user as any).uid || user.id;
      if (!authUserId) {
        setError('Failed to create account - no user ID');
        setLoading(false);
        return;
      }
      await completeSignIn(authUserId);
    } catch (err: any) {
      console.error('Setup error:', err);
      if (err.code === 'auth/email-already-in-use' || err.message?.includes('email-already-in-use')) {
        setError('This email already has an account. Please use the sign-in form below with your existing password.');
        setSetupMode(false); // Exit setup mode
      } else {
        setError(err.message || 'Failed to set up account. Please try again.');
      }
      setLoading(false);
    }
  };

  const completeSignIn = async (authUserId: string) => {
    try {
      if (!authUserId) {
        setError('Invalid authentication ID');
        setLoading(false);
        return;
      }

      // Get staff record by auth user ID
      let staff = await authService.getStaffByAuthUserId(authUserId);

      // If not found, try by email (for newly created users)
      if (!staff) {
        const { user } = await authService.getUser();
        if (user && (user as any).email) {
          staff = await authService.getStaffByEmail((user as any).email);
          
          // Link the auth user to staff record if found
          if (staff && authUserId) {
            const { staffService } = await import('../lib/database');
            const staffDocId = staff.firestoreId || staff.id;
            try {
              await staffService.update(staffDocId, {
                authUserId: authUserId,
              } as any);
              console.log('✅ Auth user linked to staff record');
            } catch (updateError) {
              console.error('Failed to link auth user');
              // Continue anyway - user can still sign in
            }
          }
        }
      }

      if (!staff) {
        setError('Staff record not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      console.log('✅ Sign-in completed successfully');
      setLoading(false);
      onSignIn(authUserId, staff);
    } catch (err) {
      console.error('Sign-in error occurred');
      setError('Failed to load user data');
      setLoading(false);
    }
  };

  const formattedTime = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const formattedDate = time.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="h-screen w-screen bg-gray-900 flex flex-col justify-between items-center font-sans text-white overflow-hidden">
      {/* Top Section - Logo and Time */}
      <div className="flex-shrink-0 pt-8 sm:pt-12 pb-4">
        {/* Logo */}
        <div className="mb-4 sm:mb-8">
          <img 
            src="/logo.png" 
            alt="KINGS PARK"
            className="max-w-xs max-h-24 sm:max-h-32 mx-auto object-contain"
            style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
        
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-widest">{formattedTime}</h1>
          <p className="text-gray-400 mt-2 text-xs sm:text-sm">{formattedDate}</p>
          <p className="text-gray-400 mt-2 sm:mt-4 text-xs sm:text-sm">Sign in to start your shift</p>
        </div>
      </div>
      
      {/* Middle Section - Form (scrollable on small screens) */}
      <div className="flex-1 overflow-y-auto w-full px-4 sm:px-8 pb-4">
        <div className="w-full max-w-md mx-auto">
        {setupMode ? (
          // Setup Mode: Set Password for First Time
          <form onSubmit={handleSetupPassword} className="space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">Set Up Your Account</h2>
              <p className="text-gray-400 text-sm">
                Welcome, {staffData?.name}! Please set a password to continue.
              </p>
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                readOnly={softKeyboardEnabled}
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
                onFocus={() => {
                  if (softKeyboardEnabled) {
                    openKeyboard({
                      id: 'setup-new-password',
                      type: 'alphanumeric',
                      value: newPassword,
                      label: 'New Password',
                      onChange: applyNewPassword,
                      maskValue: true,
                    });
                  }
                }}
                onChange={(e) => {
                  if (!softKeyboardEnabled) {
                    applyNewPassword(e.target.value);
                  }
                }}
                placeholder="Minimum 8 characters"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                disabled={loading}
                required
                minLength={8}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                readOnly={softKeyboardEnabled}
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
                onFocus={() => {
                  if (softKeyboardEnabled) {
                    openKeyboard({
                      id: 'setup-confirm-password',
                      type: 'alphanumeric',
                      value: confirmPassword,
                      label: 'Confirm Password',
                      onChange: applyConfirmPassword,
                      maskValue: true,
                    });
                  }
                }}
                onChange={(e) => {
                  if (!softKeyboardEnabled) {
                    applyConfirmPassword(e.target.value);
                  }
                }}
                placeholder="Re-enter password"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                disabled={loading}
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-3">
                <p className="text-red-200 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
              className="w-full bg-pink-500 text-white py-3 rounded-lg font-medium hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? 'Setting up...' : 'Set Password & Continue'}
            </button>

            <button
              type="button"
              onClick={() => {
                setSetupMode(false);
                setStaffData(null);
                setNewPassword('');
                setConfirmPassword('');
                setError('');
              }}
              className="w-full text-pink-400 hover:text-pink-300 text-sm mt-3"
            >
              Back to Sign In
            </button>
          </form>
        ) : (
          // Email/Password Form
          <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                readOnly={softKeyboardEnabled}
                autoComplete="email"
                data-lpignore="true"
                data-form-type="other"
                onFocus={() => {
                  if (softKeyboardEnabled) {
                    openKeyboard({
                      id: 'signin-email',
                      type: 'alphanumeric',
                      value: email,
                      label: 'Email',
                      onChange: applyEmail,
                    });
                  }
                }}
                onChange={(e) => {
                  if (!softKeyboardEnabled) {
                    applyEmail(e.target.value);
                  }
                }}
                placeholder="your.email@example.com"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                disabled={loading}
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                readOnly={softKeyboardEnabled}
                autoComplete="current-password"
                data-lpignore="true"
                data-form-type="other"
                onFocus={() => {
                  if (softKeyboardEnabled) {
                    openKeyboard({
                      id: 'signin-password',
                      type: 'alphanumeric',
                      value: password,
                      label: 'Password',
                      onChange: applyPassword,
                      maskValue: true,
                    });
                  }
                }}
                onChange={(e) => {
                  if (!softKeyboardEnabled) {
                    applyPassword(e.target.value);
                  }
                }}
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank if setting up for the first time</p>
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-3">
                <p className="text-red-200 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-pink-500 text-white py-3 rounded-lg font-medium hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? 'Signing in...' : setupMode ? 'Continue Setup' : 'Sign In'}
            </button>

            {/* <button
              type="button"
              className="w-full text-pink-400 hover:text-pink-300 text-sm mt-3"
            >
              Forgot password?
            </button> */}
          </form>
        )}
        </div>
      </div>

      {/* Footer - Fixed at bottom, responsive text size */}
      <footer className="flex-shrink-0 pb-4 sm:pb-6 text-center text-gray-500">
        <p className="font-bold text-sm sm:text-base md:text-xl">KORU POS</p>
        <p className="text-xs sm:text-sm">from TUITECH SOLUTIONS LTD</p>
      </footer>
    </div>
  );
};

export default SignInPage;

